import type { RGB } from '../../types';

export type HarmonyMode =
  | 'analogous'
  | 'monochromatic'
  | 'triad'
  | 'complementary'
  | 'split-complementary'
  | 'square'
  | 'compound'
  | 'shades'
  | 'custom';

export interface HSV {
  h: number; // 0-360
  s: number; // 0-100
  v: number; // 0-100
}

export interface WheelColor {
  hsv: HSV;
  rgb: RGB;
  hex: string;
}

export interface ColorWheelProps {
  baseColor: RGB;
  onBaseColorChange: (rgb: RGB) => void;
  onHarmonyChange: (colors: RGB[]) => void;
  initialHandleCount?: number;
  maxHandleCount?: number;
  colorName?: string;
}

export const MIN_HANDLES = 2;
export const MAX_HANDLES = 10;

export const HARMONY_MODES: { mode: HarmonyMode; label: string; description: string }[] = [
  { mode: 'analogous', label: 'Analogous', description: 'Colors adjacent on the wheel create a serene, comfortable palette. Ideal for nature-inspired designs and cohesive UI themes.' },
  { mode: 'monochromatic', label: 'Mono', description: 'Variations of a single hue through different saturations and brightness levels. Perfect for elegant, unified brand identities.' },
  { mode: 'triad', label: 'Triad', description: 'Three colors equally spaced at 120° apart offer vibrant contrast while maintaining balance. Great for playful, dynamic interfaces.' },
  { mode: 'complementary', label: 'Complement', description: 'Two hues opposite on this HSV wheel (180 degrees apart). This hue relationship does not guarantee readable text; check the actual foreground and background.' },
  { mode: 'split-complementary', label: 'Split', description: 'A base color paired with the two colors flanking its complement. Offers strong contrast with less visual tension.' },
  { mode: 'square', label: 'Square', description: 'Four colors at 90° intervals form a balanced, rich palette. Works well for complex designs needing multiple distinct accent colors.' },
  { mode: 'compound', label: 'Compound', description: 'A modified complementary scheme with additional supporting hues. Versatile for multi-section layouts and editorial design.' },
  { mode: 'shades', label: 'Shades', description: 'Progressively darker versions of the base color by reducing brightness. Essential for depth, shadows, and layered UI components.' },
  { mode: 'custom', label: 'Custom', description: 'Freely position each handle on the wheel for a completely bespoke palette tailored to your specific design needs.' },
];
